/* eslint-disable */
import { getLocale, trackMessageCall, experimentalMiddlewareLocaleSplitting, isServer, experimentalStaticLocale } from '../runtime.js';
/** @typedef {import('../runtime.js').LocalizedString} LocalizedString */

const en_rscu_intro = /** @type {(inputs: {}) => LocalizedString} */ () => {
	return /** @type {LocalizedString} */ (`Compute the Relative Synonymous Codon Usage (RSCU) of a coding sequence. RSCU measures how often a codon is used compared with what you'd expect if all synonymous codons for its amino acid were used equally — values above 1 are favoured, below 1 are avoided.`)
};

const es_rscu_intro = /** @type {(inputs: {}) => LocalizedString} */ () => {
	return /** @type {LocalizedString} */ (`Calcula el Uso Relativo de Codones Sinónimos (RSCU) de una secuencia codificante. El RSCU mide con qué frecuencia se usa un codón en comparación con lo esperado si todos los codones sinónimos de su aminoácido se usaran por igual — valores superiores a 1 están favorecidos, inferiores a 1 están evitados.`)
};

const de_rscu_intro = /** @type {(inputs: {}) => LocalizedString} */ () => {
	return /** @type {LocalizedString} */ (`Berechnen Sie die Relative Synonyme Codon-Nutzung (RSCU) einer Kodierungssequenz. RSCU misst, wie oft ein Codon im Vergleich zu dem erwarteten Wert verwendet wird, wenn alle synonymen Codons für seine Aminosäure gleich häufig verwendet würden — Werte über 1 sind bevorzugt, unter 1 werden gemieden.`)
};

const nl_rscu_intro = /** @type {(inputs: {}) => LocalizedString} */ () => {
	return /** @type {LocalizedString} */ (`Bereken de Relative Synonymous Codon Usage (RSCU) van een coderende sequentie. RSCU meet hoe vaak een codon wordt gebruikt vergeleken met wat je zou verwachten als alle synonieme codons voor zijn aminozuur even vaak zouden worden gebruikt — waarden boven 1 worden begunstigd, onder 1 worden vermeden.`)
};

const fr_rscu_intro = /** @type {(inputs: {}) => LocalizedString} */ () => {
	return /** @type {LocalizedString} */ (`Calculez l'usage relatif des codons synonymes (RSCU) d'une séquence codante. Le RSCU mesure la fréquence d'utilisation d'un codon par rapport à ce qu'on attendrait si tous les codons synonymes de son acide aminé étaient utilisés également — les valeurs supérieures à 1 sont favorisées, inférieures à 1 sont évitées.`)
};

const it_rscu_intro = /** @type {(inputs: {}) => LocalizedString} */ () => {
	return /** @type {LocalizedString} */ (`Calcola l'uso relativo dei codoni sinonimi (RSCU) di una sequenza codificante. L'RSCU misura quanto spesso un codone viene usato rispetto a quanto ci si aspetterebbe se tutti i codoni sinonimi del suo amminoacido fossero usati in modo uguale — valori sopra 1 sono favoriti, sotto 1 sono evitati.`)
};

const pt_br2_rscu_intro = /** @type {(inputs: {}) => LocalizedString} */ () => {
	return /** @type {LocalizedString} */ (`Calcule o Uso Relativo de Códons Sinônimos (RSCU) de uma sequência codificante. O RSCU mede com que frequência um códon é usado em comparação com o esperado se todos os códons sinônimos de seu aminoácido fossem usados igualmente — valores acima de 1 são favorecidos, abaixo de 1 são evitados.`)
};

const pt_rscu_intro = /** @type {(inputs: {}) => LocalizedString} */ () => {
	return /** @type {LocalizedString} */ (`Calcule o Uso Relativo de Codões Sinónimos (RSCU) de uma sequência codificante. O RSCU mede a frequência com que um codão é usado em comparação com o que seria de esperar se todos os codões sinónimos do seu aminoácido fossem usados igualmente — valores acima de 1 são favorecidos, abaixo de 1 são evitados.`)
};

const ja_rscu_intro = /** @type {(inputs: {}) => LocalizedString} */ () => {
	return /** @type {LocalizedString} */ (`コード配列の相対同義コドン使用頻度 (RSCU) を計算します。RSCUは、そのアミノ酸のすべての同義コドンが均等に使われた場合と比較して、あるコドンがどれだけ頻繁に使われているかを測定します — 1を超える値は好まれ、1未満は避けられていることを示します。`)
};

const zh_rscu_intro = /** @type {(inputs: {}) => LocalizedString} */ () => {
	return /** @type {LocalizedString} */ (`计算编码序列的相对同义密码子使用度（RSCU）。RSCU 衡量的是某密码子的实际使用频率与假设该氨基酸的所有同义密码子被均等使用时的预期频率之比——高于 1 表示受偏好，低于 1 表示被回避。`)
};

/**
* This function has been compiled by [Paraglide JS](https://inlang.com/m/gerre34r).
*
* - Changing this function will be over-written by the next build.
*
* - If you want to change the translations, you can either edit the source files e.g. `en.json`, or
* use another inlang app like [Fink](https://inlang.com/m/tdozzpar) or the [VSCode extension Sherlock](https://inlang.com/m/r7kp499g).
* 
* @param {{}} inputs
* @param {{ locale?: "en" | "es" | "de" | "nl" | "fr" | "it" | "pt-BR" | "pt" | "ja" | "zh" }} options
* @returns {LocalizedString}
*/
/* @__NO_SIDE_EFFECTS__ */
export const rscu_intro = (inputs = {}, options = {}) => {
	if (experimentalMiddlewareLocaleSplitting && isServer === false) {
		return /** @type {any} */ (globalThis).__paraglide_ssr.rscu_intro(inputs) 
	}
	const locale = experimentalStaticLocale ?? options.locale ?? getLocale()
	trackMessageCall("rscu_intro", locale)
	if (locale === "en") return en_rscu_intro(inputs)
	if (locale === "es") return es_rscu_intro(inputs)
	if (locale === "de") return de_rscu_intro(inputs)
	if (locale === "nl") return nl_rscu_intro(inputs)
	if (locale === "fr") return fr_rscu_intro(inputs)
	if (locale === "it") return it_rscu_intro(inputs)
	if (locale === "pt-BR") return pt_br2_rscu_intro(inputs)
	if (locale === "pt") return pt_rscu_intro(inputs)
	if (locale === "ja") return ja_rscu_intro(inputs)
	return zh_rscu_intro(inputs)
};